import React from 'react';

import { useServices } from 'hooks/useServices';
import { useAppState } from 'hooks/useAppState';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { usePageFocus } from 'hooks/usePageFocus';
import { ProdStackPollingAbortError } from 'utils/errors';
import { useAsyncController } from 'hooks/useAsyncController';
import { ProdStackManagerFinishComplete } from './ProdStackManagerFinishComplete';
import { ProdStackManagerFinishIntermediate } from './ProdStackManagerFinishIntermediate';
import { FadeAnimationComponent } from '../../design/fadeAnimationComponents';
import { ProdStackLoadingFallback } from './ProdStackLoadingFallback';

enum FINISH_STEPS {
  STEP_1,
  STEP_2,
}

export const ProdStackManagerFinish = () => {
  const { prodStackInfo } = useAppState();

  const { pollProdStack } = useServices();

  const { wrapAsync } = useAsyncController();

  const dispatch = useAppDispatcher();

  const pageIsFocused = usePageFocus();

  const [showFinalScreen, setShowFinalScreen] = React.useState(false);

  const handleNext = React.useCallback(() => {
    setShowFinalScreen(true);
  }, []);

  const shouldFetchProdStackInfo =
    (prodStackInfo === undefined || prodStackInfo === null) && pageIsFocused;

  React.useEffect(() => {
    if (shouldFetchProdStackInfo) {
      const abortController = new AbortController();

      const promise = pollProdStack(abortController.signal)
        .then((prodStackInfo) => {
          dispatch({
            name: 'ProdStackManager__ProdStackInfoRecieved',
            prodStackInfo,
          });
        })
        .catch((error) => {
          if (error instanceof ProdStackPollingAbortError) {
            console.warn('Prod stack polling aborted');
          } else {
            throw error;
          }
        });

      // Используется локальный async-контроллер вместо AsyncBoundary
      // чтобы отрисовка компонента не уходила в бесконечный цикл.
      wrapAsync(promise);

      return () => {
        abortController.abort();
      };
    }
  }, [pollProdStack, dispatch, wrapAsync, shouldFetchProdStackInfo]);

  const currentStep = React.useMemo(() => {
    if (showFinalScreen) {
      return FINISH_STEPS.STEP_2;
    }
    return FINISH_STEPS.STEP_1;
  }, [showFinalScreen]);

  const content = React.useMemo(() => {
    if (!prodStackInfo) {
      return null;
    }

    switch (currentStep) {
      case FINISH_STEPS.STEP_1:
        return <ProdStackManagerFinishIntermediate onNext={handleNext} />;
      case FINISH_STEPS.STEP_2:
        return <ProdStackManagerFinishComplete prodStackInfo={prodStackInfo} />;
      default:
        return null;
    }
  }, [currentStep, handleNext, prodStackInfo]);

  if (!prodStackInfo) {
    return <ProdStackLoadingFallback />;
  }

  return (
    <FadeAnimationComponent trigger={currentStep}>
      {content}
    </FadeAnimationComponent>
  );
};
